// Front matter for the guides (src/content/guides/<slug>/<lang>.md): a small YAML subset, parsed here so the guides
// need no YAML dependency. Supported, and all a guide needs:
//
//   key: value                 a string ("double quotes" optional; use them when the value starts with a symbol)
//   key:                       a list of strings
//     - value
//   key:                       a list of question/answer pairs
//     - q: Question?
//       a: Answer.
//
// One value per line (no folded / multi-line strings). Anything else is an error, so a typo fails the build's tests
// instead of silently dropping a field.

export type FrontMatterValue = string | string[] | { q: string; a: string }[];
export type FrontMatter = Record<string, FrontMatterValue>;

const unquote = (raw: string): string => {
  const v = raw.trim();
  if (v.startsWith('"') && v.endsWith('"') && v.length >= 2) return JSON.parse(v) as string;
  return v;
};

/** Splits a guide file into its front matter and its Markdown body. */
export function splitFrontMatter(src: string): { data: FrontMatter; body: string } {
  const text = src.replace(/\r\n?/g, "\n");
  const m = /^---\n([\s\S]*?)\n---\n?/.exec(text);
  if (!m) throw new Error("guide has no front matter (--- … ---)");
  return { data: parseFrontMatter(m[1]), body: text.slice(m[0].length) };
}

export function parseFrontMatter(block: string): FrontMatter {
  const data: FrontMatter = {};
  const lines = block.split("\n");
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim() || line.trim().startsWith("#")) {
      i += 1;
      continue;
    }
    const kv = /^([A-Za-z][\w]*):(?:\s+(.*))?$/.exec(line);
    if (!kv) throw new Error(`front matter: can't read line ${i + 1}: ${line}`);
    const [, key, value] = kv;
    i += 1;
    if (value !== undefined && value.trim() !== "") {
      data[key] = unquote(value);
      continue;
    }
    // A list: "- value" items, or "- q: …" / "  a: …" pairs.
    const items: string[] = [];
    const pairs: { q: string; a: string }[] = [];
    while (i < lines.length && /^\s+-\s/.test(lines[i])) {
      const item = lines[i].replace(/^\s+-\s+/, "");
      i += 1;
      const q = /^q:\s+(.*)$/.exec(item);
      if (q) {
        const a = i < lines.length ? /^\s+a:\s+(.*)$/.exec(lines[i]) : null;
        if (!a) throw new Error(`front matter: "${key}" question without an answer: ${item}`);
        i += 1;
        pairs.push({ q: unquote(q[1]), a: unquote(a[1]) });
      } else {
        items.push(unquote(item));
      }
    }
    if (pairs.length && items.length) throw new Error(`front matter: "${key}" mixes questions and plain items`);
    data[key] = pairs.length ? pairs : items;
  }
  return data;
}
